import {
  useGetLinkTokenForConnectionMutation,
  useUpdatePlaidAccessTokenProductsMutation,
} from '@transactions/graphql';
import * as React from 'react';
import {usePlaidLink} from 'react-plaid-link';
import {Button} from 'src/Button';
import Checkbox from 'src/Checkbox/Checkbox';
import {Modal} from 'src/Modal';
import type {PlaidLinkOnSuccessMetadata} from 'react-plaid-link';
import type {SystemProps} from 'src/Modals';

const ALL_PRODUCTS = [
  {
    id: 'transactions',
    label: 'Transactions',
    description: 'Sync checking, savings, and credit card transactions',
  },
  {
    id: 'investments',
    label: 'Investments',
    description: 'Sync brokerage, 401k, and IRA account holdings',
  },
] as const;

export type ManageConnectionModalUserProps = {
  plaidAccessTokenId: string;
  institutionName?: string | null;
  currentProducts?: string[] | null;
  onSuccess?: () => void;
};

type Step = 'product-selection' | 'plaid-link';

export const ManageConnectionModal = (
  props: ManageConnectionModalUserProps & SystemProps,
) => {
  // Default to ['transactions'] for old tokens that don't have products field set
  // (All old tokens were transactions-only since that was the only product before)
  const currentProducts =
    props.currentProducts && props.currentProducts.length > 0
      ? props.currentProducts
      : ['transactions'];
  const [step, setStep] = React.useState<Step>('product-selection');
  const [linkToken, setLinkToken] = React.useState<string | null>(null);

  // Track selected products - start with current products checked
  const [selectedProducts, setSelectedProducts] = React.useState<Set<string>>(
    () => new Set(currentProducts),
  );

  const [getLinkToken, {loading: linkTokenLoading}] =
    useGetLinkTokenForConnectionMutation();

  const [updateProducts] = useUpdatePlaidAccessTokenProductsMutation();

  const handlePlaidSuccess = React.useCallback(
    (_publicToken: string, _metadata: PlaidLinkOnSuccessMetadata) => {
      // Persist the updated products to the PlaidAccessToken record
      const allSelectedProducts = Array.from(selectedProducts);
      updateProducts({
        variables: {
          id: props.plaidAccessTokenId,
          products: allSelectedProducts,
        },
      }).then(() => {
        props.onSuccess?.();
        props.onRequestClose();
      });
    },
    [props, selectedProducts, updateProducts],
  );

  const handlePlaidExit = React.useCallback(() => {
    setStep('product-selection');
    setLinkToken(null);
  }, []);

  const {open, ready} = usePlaidLink({
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit: handlePlaidExit,
  });

  // Auto-open Plaid when ready and in plaid-link step
  React.useEffect(() => {
    if (ready && step === 'plaid-link') {
      open();
    }
  }, [ready, step, open]);

  const toggleProduct = (productId: string) => {
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleUpdate = () => {
    // Only pass products that are NEW (not already authorized)
    // For pure reauth, pass null so Plaid doesn't show institution picker
    const newProducts = Array.from(selectedProducts).filter(
      (p) => !currentProducts.includes(p),
    );
    getLinkToken({
      variables: {
        plaidAccessTokenId: props.plaidAccessTokenId,
        products: newProducts.length > 0 ? newProducts : null,
      },
    }).then((result) => {
      if (result.data?.getLinkTokenForConnection) {
        setLinkToken(result.data.getLinkTokenForConnection);
        setStep('plaid-link');
      }
    });
  };

  const hasNewProducts = ALL_PRODUCTS.some(
    (p) => selectedProducts.has(p.id) && !currentProducts.includes(p.id),
  );

  if (step === 'plaid-link') {
    // Show a simple waiting state while Plaid popup is open
    return (
      <Modal
        onRequestClose={props.onRequestClose}
        header={`Manage ${props.institutionName ?? 'Connection'}`}
      >
        <div className="flex flex-col items-center gap-4 py-8">
          <p className="text-muted-foreground">
            Complete the authentication in the Plaid window...
          </p>
          <Button variant="outline" onClick={handlePlaidExit}>
            Cancel
          </Button>
        </div>
      </Modal>
    );
  }

  const canUpdate = selectedProducts.size > 0;
  const footer = (
    <>
      <Button variant="outline" onClick={props.onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        disabled={!canUpdate || linkTokenLoading}
        onClick={handleUpdate}
      >
        {linkTokenLoading
          ? 'Loading...'
          : hasNewProducts
            ? 'Add Products'
            : 'Re-authenticate'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      header={`Manage ${props.institutionName ?? 'Connection'}`}
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        <p className="text-muted-foreground">
          Select which products to authorize. Check additional products to add
          them, or click Re-authenticate to refresh your credentials.
        </p>
        <div className="flex flex-col gap-3">
          {ALL_PRODUCTS.map((product) => {
            const isAuthorized = currentProducts.includes(product.id);
            const isSelected = selectedProducts.has(product.id);

            return (
              <div
                key={product.id}
                className="flex cursor-pointer items-center gap-3"
                onClick={() => toggleProduct(product.id)}
              >
                <Checkbox
                  value={isSelected}
                  onChange={() => toggleProduct(product.id)}
                />
                <div className="flex flex-1 flex-col">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{product.label}</span>
                    {isAuthorized && (
                      <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900 dark:text-green-300">
                        Authorized
                      </span>
                    )}
                  </div>
                  <span className="text-muted-foreground text-sm">
                    {product.description}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};
